import { createBrowserRouter, useNavigation, Navigate, Outlet, useLocation, matchPath } from 'react-router';
import * as Sentry from '@sentry/react';
import FlashMessage from 'react-native-flash-message';

import { ErrorPage } from '~/pages/ErrorPage';
import { WelcomePage, SetupPage } from '~/pages/setup';
import { Review } from '~/pages/review';
import { OldLoginPage } from '~/pages/OldLoginPage';
import { LoginPage } from '~/pages/LoginPage';
import { SignupPage } from '~/pages/SignupPage';
import { VerifyPage } from '~/pages/VerifyPage';
import { FlashBanner } from '~/components/FlashBanner';
import { useEmailPasswordSignupLoginExperienceFeature } from '~/featureFlags';
import { createLogger } from '~/utils/logging';
import { useUserProfile } from '~/services/useUserProfileQuery';
import { useTokenInitialization } from '~/hooks/useTokenInitialization';
import { AuthenticationError } from '~/errors';
import { Loading } from '~/components/Loading';
import { UserProfile } from '~/services/types';

import { sessionStore } from './store/sessionStore';

const log = createLogger('AppRouter');

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export function createAppRouter() {
  return sentryCreateBrowserRouter([
    {
      element: <RootLayout />,
      children: [
        {
          index: true,
          element: <Index />,
        },
        {
          element: <PublicLayout />,
          children: [
            {
              path: '/login',
              element: <LoginRoute />,
            },
            {
              path: '/signup',
              element: <SignupRoute />,
            },
          ],
        },
        {
          // Standalone route — accessible to both unverified authenticated users and unauthenticated users.
          path: '/verify',
          element: <VerifyRoute />,
        },
        {
          element: <AuthLayout />,
          children: [
            {
              path: '/setup',
              element: <SetupPage />,
            },
            {
              path: '/welcome',
              element: <WelcomePage />,
            },
            {
              path: '/review/:periodShortName?',
              Component: Review,
            },
            {
              path: '/home',
              element: <Navigate to="/review" replace />, // backwards compat for old home route
            },
          ],
        },
        {
          path: '*',
          element: <ErrorPage />,
        },
      ],
      errorElement: <ErrorPage />,
    },
  ]);
}

function RootLayout() {
  const navigation = useNavigation();
  const location = useLocation();
  const isNavigating = Boolean(navigation.location);
  const { isInitialized } = useTokenInitialization();
  const { user, isLoading: isUserLoading } = useUserProfile();

  log.debug('Root layout rendering', {
    isNavigating,
    isInitialized,
    location: location.pathname,
    user,
    isUserLoading,
  });

  // Show the global loading indicator if:
  // - TokenManager is not yet initialized
  // - We're in the middle of a navigation
  // - We're waiting for the user profile query
  if (!isInitialized || isNavigating || isUserLoading) {
    // TOOD: Demote me to log.debug once we've finished debugging in prod.
    log.info('Showing loading indicator', {
      isInitialized,
      isNavigating,
      isUserLoading,
    });
    return <Loading />;
  }

  // Otherwise, render the current child route.
  return (
    <>
      <Outlet />
      <FlashMessage
        position="top"
        animationDuration={100}
        duration={10000}
        MessageComponent={({ message }: { message: { message: string; type: 'warning' | 'success' } }) => (
          <FlashBanner message={message} />
        )}
      />
    </>
  );
}

function Index() {
  const { user } = useUserProfile();
  const target = getInitialRoute(user);
  log.info('Navigating to initial route', { route: target });
  return <Navigate to={target} replace />;
}

function getInitialRoute(user?: UserProfile) {
  if (!user) {
    return '/login';
  }

  if (!user?.isSetupComplete) {
    return '/setup';
  }

  return '/review';
}

function PublicLayout() {
  const { user } = useUserProfile();
  const location = useLocation();
  const path = location.pathname;

  // If the user is already logged in, redirect them to the correct authorized route.
  if (user) {
    const target = getInitialRoute(user);
    log.info('Navigating to different route because already logged in', {
      route: target,
      originalRoute: path,
    });
    return <Navigate to={target} replace />;
  }

  // Otherwise, continue to render the public route.
  return <Outlet />;
}

function AuthLayout() {
  const { user, error: userError } = useUserProfile();
  const isSetupSubmitted = sessionStore((store) => store.isSetupSubmitted);
  const location = useLocation();
  const path = location.pathname;

  // If the user's session has expired, redirect to the login route.
  const hasSessionExpired = userError && userError instanceof AuthenticationError;
  const isAuthorized = user && !hasSessionExpired;
  if (!isAuthorized) {
    log.info('Navigating to login route because session has expired', {
      route: '/login',
      originalRoute: path,
    });
    return <Navigate to="/login" replace />;
  }

  // If the user's email is not verified, redirect to the verification page.
  if (user.emailVerified === false) {
    log.info('Navigating to verify route because email is not verified', {
      route: '/verify',
      originalRoute: path,
    });
    return <Navigate to="/verify" replace />;
  }

  // If the user has tried navigating directly to an authorized route but hasn't completed setup, redirect to the setup route.
  const needsSetup = !user?.isSetupComplete && !isSetupSubmitted;
  if (needsSetup && !matchPath(path, '/setup')) {
    log.info('Navigating to setup route because user has not completed setup', {
      route: '/setup',
      originalRoute: path,
    });
    return <Navigate to="/setup" replace />;
  }

  // If the user has tried navigating directly to the setup page but has already completed setup, redirect to the home route.
  const doesNotNeedSetup = user?.isSetupComplete;
  if (doesNotNeedSetup && matchPath(path, '/setup')) {
    if (isSetupSubmitted) {
      log.info('Navigating to welcome route because user has completed setup and setup is submitted', {
        route: '/welcome',
        originalRoute: path,
      });
      return <Navigate to="/welcome" replace />;
    } else {
      log.info('Navigating to home route because user has completed setup', {
        route: '/review',
        originalRoute: path,
      });
      return <Navigate to="/review" replace />;
    }
  }

  // Otherwise, continue to render the authorized route.
  return <Outlet />;
}

function LoginRoute() {
  const isFeatureEnabled = useEmailPasswordSignupLoginExperienceFeature();
  return isFeatureEnabled ? <LoginPage /> : <OldLoginPage />;
}

function SignupRoute() {
  const isFeatureEnabled = useEmailPasswordSignupLoginExperienceFeature();
  return isFeatureEnabled ? <SignupPage /> : <Navigate to="/login" replace />;
}

function VerifyRoute() {
  const isFeatureEnabled = useEmailPasswordSignupLoginExperienceFeature();
  return isFeatureEnabled ? <VerifyPage /> : <Navigate to="/login" replace />;
}
